import { Box, Flex, Text } from '@chakra-ui/react';
import { WarningTriangle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import {
  UGC_BRIEF_MAX_END_DAYS,
  ugcMinimumPaymentCents,
  type BriefDeliverable,
} from '@fluenceur/types';
import type { CollabPaidBriefViolation } from './collab-paid-brief-rules';

const KEY = 'collabs.compose';

interface CollabPaidBriefBlockersProps {
  violations: CollabPaidBriefViolation[];
  /** The contract currency, named in the cap sentence. */
  currency: string;
  /** The brief's deliverables, which a UGC minimum is counted from. */
  deliverables?: BriefDeliverable[];
}

/**
 * Why this paid brief cannot be sent, stated where the send is refused.
 *
 * One line per bound, so a brand that broke two rules fixes both in one trip
 * rather than discovering the second after correcting the first. The cap names
 * the contract's own currency: the bound is on the amount agreed, not on its
 * conversion into euro.
 */
export const CollabPaidBriefBlockers = ({
  violations,
  currency,
  deliverables = [],
}: CollabPaidBriefBlockersProps) => {
  const { t } = useTranslation();

  if (violations.length === 0) return null;

  return (
    <Flex
      direction="column"
      gap="4px"
      bg="red.subtle"
      border="1px solid"
      borderColor="red.200"
      rounded="lg"
      px="14px"
      py="10px"
      data-testid="collab-paid-brief-blockers"
    >
      {violations.map((violation) => (
        <Flex key={violation} align="flex-start" gap="8px">
          <Box display="flex" color="red.700" mt="2px" flexShrink={0}>
            <WarningTriangle width={13} height={13} aria-hidden="true" />
          </Box>
          <Text fontSize="12px" color="red.700" lineHeight="1.5">
            {violation === 'amountAboveCap'
              ? t(`${KEY}.capExceeded`, { currency })
              : violation === 'ugcBelowMinimum'
                ? t(`${KEY}.ugcBelowMinimum`, {
                    currency,
                    minimum: ugcMinimumPaymentCents(deliverables) / 100,
                  })
                : violation === 'ugcEndDeadlineTooFar'
                  ? t(`${KEY}.ugcEndDeadlineTooFar`, { days: UGC_BRIEF_MAX_END_DAYS })
                  : t(`${KEY}.${violation}`)}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};
